/**
 * Rede Proto — camada de service (API real).
 *
 * Consome os endpoints `/network/*` do backend (escopados ao hive_owner logado
 * pelo token) e normaliza o formato wire para os tipos de domínio das telas:
 * - `uf` int → sigla; `region` enum minúsculo → label;
 * - percentuais 0–100; CSAT 0–5; campos ausentes chegam `null`.
 *
 * Todas as funções resolvem no mesmo envelope { detail, data } do restante da
 * API. Em erro HTTP (401/404/422) preenchem `detail.description`, que os hooks
 * já tratam como falha.
 */
import {
  REGION_ENUM_BY_LABEL,
  UF_INT_BY_SIGLA,
  UF_REGION,
  ufSiglaFromInt,
  regionLabelFromEnum,
} from '../../../constants/redeProto';
import { handleUrl, myHeadersWithTokenContentType } from '../../global';
import {
  ActionPlan,
  ApiResponse,
  CsAccredited,
  CsDetail,
  CsDetailPole,
  CsManager,
  EvolutionPoint,
  FunnelStage,
  Goal,
  GoalInput,
  InAccreditationItem,
  NetworkOverview,
  NetworkPole,
  NoticeOption,
  PoleClassification,
  PoleOption,
  Region,
  RegionDistribution,
  RegionEnum,
  RejectionReason,
  RequiredFile,
  StateSummary,
  Typology,
  TypologyOption,
} from './types';

/* ------------------------------------------------------------------ */
/* Camada HTTP                                                         */
/* ------------------------------------------------------------------ */

const apiBase = (): string => `${handleUrl()}/api/V1`;

const messageForStatus = (status: number): string => {
  if (status === 401) return 'Sessão sem permissão de hive_owner.';
  if (status === 403) {
    return (
      'Acesso não liberado ao dashboard. Se você é CS, ' +
      'verifique se sua carteira está configurada.'
    );
  }
  if (status === 404) return 'Recurso não encontrado.';
  if (status === 422) return 'Dados inválidos para a operação.';
  return 'Não foi possível carregar os dados da rede.';
};

async function request<T>(
  path: string,
  init?: RequestInit
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${apiBase()}${path}`, {
      ...init,
      headers: myHeadersWithTokenContentType(),
    });

    if (!response.ok) {
      let description = messageForStatus(response.status);
      try {
        const body = await response.json();
        if (body?.detail?.description) description = body.detail.description;
      } catch {
        /* corpo vazio ou não-JSON — mantém a mensagem padrão do status */
      }
      return {
        detail: { status: 'error', status_code: response.status, description },
      };
    }

    const text = await response.text();
    if (!text) {
      return { detail: { status: 'ok', status_code: 0 }, data: undefined };
    }
    return JSON.parse(text) as ApiResponse<T>;
  } catch {
    return {
      detail: {
        status: 'error',
        status_code: 0,
        description: 'Falha de conexão com o servidor.',
      },
    };
  }
}

/**
 * Query string do edital selecionado no seletor global do RedeProto.
 * `null`/`undefined` (= "Todos os editais") → sem filtro (comportamento antigo).
 */
const noticeQuery = (noticeId?: number | null): string =>
  noticeId != null ? `?notice_id=${noticeId}` : '';

/** Remapeia o envelope preservando `detail` e transformando apenas `data`. */
const mapData = <R, T>(
  res: ApiResponse<R>,
  transform: (data: R) => T
): ApiResponse<T> =>
  res.data == null
    ? { detail: res.detail, data: undefined }
    : { detail: res.detail, data: transform(res.data) };

/* ------------------------------------------------------------------ */
/* Shapes wire (snake_case) do backend                                 */
/* ------------------------------------------------------------------ */

interface RawOverview {
  total_poles: number;
  total_states: number;
  total_goal: number;
  total_accredited: number;
  overall_achievement: number;
  covered_cities: number;
  regions_below_goal: number;
  critical_poles: number;
  network_sla: number | null;
  first_review_approval: number | null;
  avg_csat: number | null;
}

interface RawState {
  uf: number;
  goal: number;
  accredited: number;
  achievement: number | null;
  poles_count: number;
  covered_cities: number;
}

interface RawRegion {
  region: RegionEnum;
  goal: number;
  accredited: number;
  achievement: number | null;
}

interface RawPole {
  pole_id: number;
  name: string | null;
  uf: number;
  region: RegionEnum | null;
  city: string;
  latitude: number | null;
  longitude: number | null;
  sla: number | null;
  approval: number | null;
  accredited: number | null;
  goal: number | null;
  achievement: number | null;
  csat: number | null;
  classification: PoleClassification | null;
}

interface RawPoleOption {
  id: number;
  name: string | null;
  uf: number;
  city: string;
  notice_name: string | null;
}

interface RawActionPlan {
  notice_coverage_id: number;
  plan: string;
}

interface RawEvolution {
  month: string;
  total_demands: number;
  delivered_on_time: number;
  sla_pct: number | null;
  approved_first_review: number;
  approval_pct: number | null;
  avg_csat: number | null;
}

interface RawFunnel {
  lead_identificado: number;
  contatado: number;
  em_cadastro: number;
  documentacao_pendente: number;
  em_validacao: number;
  aprovado: number;
  ativo: number;
  reprovado: number;
  inativo: number;
}

interface RawCsManager {
  id: number;
  name: string;
  region: RegionEnum | null;
  territories: string | null;
  poles_count: number;
  accredited: number;
  demands: number;
  sla: number | null;
  approval: number | null;
  csat: number | null;
  overall_score: number | null;
}

interface RawCsDetailPole {
  pole_id: number;
  name: string | null;
  uf: number;
  city: string;
  sla: number | null;
  csat: number | null;
  approval: number | null;
  delayed: number;
}

interface RawCsAccredited {
  licensed_company_id: number;
  name: string | null;
  uf: number | null;
  demands: number;
  sla: number | null;
  approval: number | null;
  csat: number | null;
}

interface RawCsDetail {
  manager: RawCsManager;
  poles: RawCsDetailPole[];
  accredited: RawCsAccredited[];
}

interface RawRejectionReason {
  reason: string;
  occurrences: number;
  pct_of_total: number;
}

interface RawTypology {
  typology_group_id: number;
  name: string;
  notice_name: string | null;
  price: number | null;
  real_estate_types: string[];
  qualified_accredited: number;
  goal: number | null;
  achievement: number | null;
  uncovered_regions: RegionEnum[];
}

interface RawTypologyOption {
  id: number;
  name: string;
  notice_name: string | null;
}

interface RawGoal {
  metric_slug: Goal['metricSlug'];
  dimension_type: Goal['dimensionType'];
  polo_id: number | null;
  typology_group_id: number | null;
  uf: number | null;
  regiao: RegionEnum | null;
  period_type: Goal['periodType'];
  period_number: number | null;
  year: number;
  target_value: number;
}

interface RawRequiredFile {
  file_name: string;
  status: RequiredFile['status'];
}

interface RawInAccreditationItem {
  notice_company_id: number;
  company_name: string;
  cnpj: string;
  requested_at: string | null;
  notice_name: string | null;
  abandoned: boolean;
  juridico: RawRequiredFile[];
  engenharia: RawRequiredFile[];
  ativo: InAccreditationItem['ativo'];
  plano: string | null;
}

/* ------------------------------------------------------------------ */
/* Normalizadores                                                      */
/* ------------------------------------------------------------------ */

const regionsToLabels = (regions: RegionEnum[] | null | undefined): Region[] =>
  (regions ?? [])
    .map((region) => regionLabelFromEnum(region))
    .filter((region): region is Region => region !== null);

const toPole = (raw: RawPole): NetworkPole => ({
  poleId: raw.pole_id,
  name: raw.name,
  city: raw.city,
  uf: ufSiglaFromInt(raw.uf),
  region: regionLabelFromEnum(raw.region),
  latitude: raw.latitude,
  longitude: raw.longitude,
  goal: raw.goal,
  accredited: raw.accredited,
  achievement: raw.achievement,
  sla: raw.sla,
  csat: raw.csat,
  approval: raw.approval,
  classification: raw.classification,
});

const toCsManager = (raw: RawCsManager): CsManager => ({
  id: raw.id,
  name: raw.name,
  region: regionLabelFromEnum(raw.region),
  territories: raw.territories,
  polesCount: raw.poles_count,
  accredited: raw.accredited,
  demands: raw.demands,
  sla: raw.sla,
  approval: raw.approval,
  csat: raw.csat,
  overallScore: raw.overall_score,
});

const toCsDetailPole = (raw: RawCsDetailPole): CsDetailPole => ({
  poleId: raw.pole_id,
  name: raw.name,
  uf: ufSiglaFromInt(raw.uf),
  city: raw.city,
  sla: raw.sla,
  csat: raw.csat,
  approval: raw.approval,
  delayed: raw.delayed,
});

const toCsAccredited = (raw: RawCsAccredited): CsAccredited => ({
  licensedCompanyId: raw.licensed_company_id,
  name: raw.name,
  uf: raw.uf == null ? null : ufSiglaFromInt(raw.uf),
  demands: raw.demands,
  sla: raw.sla,
  approval: raw.approval,
  csat: raw.csat,
});

const toTypology = (raw: RawTypology): Typology => ({
  typologyGroupId: raw.typology_group_id,
  name: raw.name,
  noticeName: raw.notice_name,
  price: raw.price,
  realEstateTypes: raw.real_estate_types ?? [],
  qualifiedAccredited: raw.qualified_accredited,
  goal: raw.goal,
  achievement: raw.achievement,
  uncoveredRegions: regionsToLabels(raw.uncovered_regions),
});

/** Ordem dos estágios do backend, mapeada para o enum interno FunnelStage. */
const FUNNEL_KEY_TO_STAGE: [keyof RawFunnel, FunnelStage][] = [
  ['lead_identificado', 'lead'],
  ['contatado', 'contacted'],
  ['em_cadastro', 'registering'],
  ['documentacao_pendente', 'docs_pending'],
  ['em_validacao', 'validating'],
  ['aprovado', 'approved'],
  ['ativo', 'active'],
  ['reprovado', 'rejected'],
  ['inativo', 'inactive'],
];

const toFunnelCounts = (raw: RawFunnel): Record<FunnelStage, number> =>
  FUNNEL_KEY_TO_STAGE.reduce<Record<FunnelStage, number>>(
    (acc, [key, stage]) => {
      acc[stage] = raw[key] ?? 0;
      return acc;
    },
    {} as Record<FunnelStage, number>
  );

const toRequiredFile = (raw: RawRequiredFile): RequiredFile => ({
  fileName: raw.file_name,
  status: raw.status,
});

const toInAccreditationItem = (
  raw: RawInAccreditationItem
): InAccreditationItem => ({
  noticeCompanyId: raw.notice_company_id,
  companyName: raw.company_name,
  cnpj: raw.cnpj,
  requestedAt: raw.requested_at,
  noticeName: raw.notice_name,
  abandoned: raw.abandoned,
  juridico: raw.juridico.map(toRequiredFile),
  engenharia: raw.engenharia.map(toRequiredFile),
  ativo: raw.ativo,
  plano: raw.plano,
});

/* ------------------------------------------------------------------ */
/* Endpoints                                                           */
/* ------------------------------------------------------------------ */

export const getNoticesOptions = async (): Promise<
  ApiResponse<NoticeOption[]>
> => request<NoticeOption[]>('/network/notices/options');

export const getNetworkOverview = async (
  noticeId?: number | null
): Promise<ApiResponse<NetworkOverview>> =>
  mapData(
    await request<RawOverview>(`/network/overview${noticeQuery(noticeId)}`),
    (raw): NetworkOverview => ({
      totalPoles: raw.total_poles,
      totalStates: raw.total_states,
      totalGoal: raw.total_goal,
      totalAccredited: raw.total_accredited,
      overallAchievement: raw.overall_achievement,
      coveredCities: raw.covered_cities,
      criticalPoles: raw.critical_poles,
      regionsBelowGoal: raw.regions_below_goal,
      networkSla: raw.network_sla,
      avgCsat: raw.avg_csat,
      firstReviewApproval: raw.first_review_approval,
    })
  );

export const getStates = async (
  noticeId?: number | null
): Promise<ApiResponse<StateSummary[]>> =>
  mapData(
    await request<RawState[]>(`/network/states${noticeQuery(noticeId)}`),
    (rows) =>
      rows.map((raw): StateSummary => {
        const sigla = ufSiglaFromInt(raw.uf);
        return {
          uf: sigla,
          ufCode: raw.uf,
          // /network/states não traz região; derivamos pela sigla.
          region: UF_REGION[sigla] ?? 'Sudeste',
          goal: raw.goal,
          accredited: raw.accredited,
          achievement: raw.achievement,
          polesCount: raw.poles_count,
          coveredCities: raw.covered_cities,
        };
      })
  );

export const getRegionDistribution = async (
  noticeId?: number | null
): Promise<ApiResponse<RegionDistribution[]>> =>
  mapData(
    await request<RawRegion[]>(`/network/regions${noticeQuery(noticeId)}`),
    (rows) =>
      rows.map(
        (raw): RegionDistribution => ({
          region: regionLabelFromEnum(raw.region) ?? 'Sudeste',
          goal: raw.goal,
          accredited: raw.accredited,
          achievement: raw.achievement,
        })
      )
  );

export const getPoles = async (
  noticeId?: number | null
): Promise<ApiResponse<NetworkPole[]>> =>
  mapData(
    await request<RawPole[]>(`/network/poles${noticeQuery(noticeId)}`),
    (rows) => rows.map(toPole)
  );

/** Polos críticos: deriva de /network/poles (sem endpoint próprio). */
export const getCriticalPoles = async (
  noticeId?: number | null
): Promise<ApiResponse<NetworkPole[]>> => {
  const res = await getPoles(noticeId);
  return mapData(res, (poles) =>
    poles.filter(
      (pole) =>
        pole.classification === 'critical' ||
        pole.classification === 'attention'
    )
  );
};

export const getPoleOptions = async (
  noticeId?: number | null
): Promise<ApiResponse<PoleOption[]>> =>
  mapData(
    await request<RawPoleOption[]>(
      `/network/poles/options${noticeQuery(noticeId)}`
    ),
    (rows) =>
      rows.map(
        (raw): PoleOption => ({
          id: raw.id,
          name: raw.name,
          uf: ufSiglaFromInt(raw.uf),
          city: raw.city,
          noticeName: raw.notice_name,
        })
      )
  );

export const getActionPlan = async (
  poleId: number
): Promise<ApiResponse<ActionPlan>> =>
  mapData(
    await request<RawActionPlan>(`/network/poles/${poleId}/action-plan`),
    (raw): ActionPlan => ({ poleId: raw.notice_coverage_id, plan: raw.plan })
  );

export const saveActionPlan = async (
  poleId: number,
  plan: string
): Promise<ApiResponse<ActionPlan>> =>
  mapData(
    await request<RawActionPlan>(`/network/poles/${poleId}/action-plan`, {
      method: 'PUT',
      body: JSON.stringify({ plan }),
    }),
    (raw): ActionPlan => ({ poleId: raw.notice_coverage_id, plan: raw.plan })
  );

export const getEvolution = async (
  months?: number,
  noticeId?: number | null
): Promise<ApiResponse<EvolutionPoint[]>> => {
  const params = new URLSearchParams();
  if (months) params.append('months', String(months));
  if (noticeId != null) params.append('notice_id', String(noticeId));
  const query = params.toString() ? `?${params.toString()}` : '';
  return mapData(
    await request<RawEvolution[]>(`/network/evolution${query}`),
    (rows) =>
      rows.map(
        (raw): EvolutionPoint => ({
          month: raw.month,
          totalDemands: raw.total_demands,
          deliveredOnTime: raw.delivered_on_time,
          slaPct: raw.sla_pct,
          approvedFirstReview: raw.approved_first_review,
          approvalPct: raw.approval_pct,
          avgCsat: raw.avg_csat,
        })
      )
  );
};

export const getAccreditationFunnel = async (
  region?: Region | 'all',
  uf?: string,
  noticeId?: number | null
): Promise<ApiResponse<Record<FunnelStage, number>>> => {
  const params = new URLSearchParams();
  if (region && region !== 'all')
    params.append('region', REGION_ENUM_BY_LABEL[region]);
  if (uf && uf !== 'all' && UF_INT_BY_SIGLA[uf]) {
    params.append('uf', String(UF_INT_BY_SIGLA[uf]));
  }
  if (noticeId != null) params.append('notice_id', String(noticeId));
  const query = params.toString() ? `?${params.toString()}` : '';
  return mapData(
    await request<RawFunnel>(`/network/accreditation-funnel${query}`),
    toFunnelCounts
  );
};

export const getCsManagers = async (
  noticeId?: number | null
): Promise<ApiResponse<CsManager[]>> =>
  mapData(
    await request<RawCsManager[]>(`/network/cs${noticeQuery(noticeId)}`),
    (rows) => rows.map(toCsManager)
  );

export const getCsDetail = async (
  csId: number,
  noticeId?: number | null
): Promise<ApiResponse<CsDetail>> =>
  mapData(
    await request<RawCsDetail>(`/network/cs/${csId}${noticeQuery(noticeId)}`),
    (raw): CsDetail => ({
      manager: toCsManager(raw.manager),
      poles: raw.poles.map(toCsDetailPole),
      accredited: raw.accredited.map(toCsAccredited),
    })
  );

export const getRejectionReasons = async (
  noticeId?: number | null
): Promise<ApiResponse<RejectionReason[]>> =>
  mapData(
    await request<RawRejectionReason[]>(
      `/network/rejection-reasons${noticeQuery(noticeId)}`
    ),
    (rows) =>
      rows.map(
        (raw): RejectionReason => ({
          reason: raw.reason,
          occurrences: raw.occurrences,
          pctOfTotal: raw.pct_of_total,
        })
      )
  );

export const getTypologies = async (
  noticeId?: number | null
): Promise<ApiResponse<Typology[]>> =>
  mapData(
    await request<RawTypology[]>(`/network/typologies${noticeQuery(noticeId)}`),
    (rows) => rows.map(toTypology)
  );

export const getTypologyOptions = async (
  noticeId?: number | null
): Promise<ApiResponse<TypologyOption[]>> =>
  mapData(
    await request<RawTypologyOption[]>(
      `/network/typologies/options${noticeQuery(noticeId)}`
    ),
    (rows) =>
      rows.map(
        (raw): TypologyOption => ({
          id: raw.id,
          name: raw.name,
          noticeName: raw.notice_name,
        })
      )
  );

export const getGoals = async (): Promise<ApiResponse<Goal[]>> =>
  mapData(await request<RawGoal[]>('/network/goals'), (rows) =>
    rows.map(
      (raw): Goal => ({
        metricSlug: raw.metric_slug,
        dimensionType: raw.dimension_type,
        poloId: raw.polo_id,
        typologyGroupId: raw.typology_group_id,
        uf: raw.uf,
        regiao: raw.regiao,
        periodType: raw.period_type,
        periodNumber: raw.period_number,
        year: raw.year,
        targetValue: raw.target_value,
      })
    )
  );

/** GET /network/in-accreditation — ordenado/paginado no servidor. */
export const getInAccreditation = async (
  noticeId: number | null,
  page: number,
  size: number
): Promise<ApiResponse<InAccreditationItem[]>> => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  if (noticeId != null) params.append('notice_id', String(noticeId));
  return mapData(
    await request<RawInAccreditationItem[]>(
      `/network/in-accreditation?${params.toString()}`
    ),
    (rows) => rows.map(toInAccreditationItem)
  );
};

export const createGoal = async (
  input: GoalInput
): Promise<ApiResponse<{ id: number }>> => {
  const body: Record<string, unknown> = {
    metric_slug: input.metricSlug ?? 'credenciados_ativos',
    dimension_type: input.dimensionType,
    period_type: input.periodType,
    year: input.year,
    target_value: input.targetValue,
  };
  if (input.poloId != null) body.polo_id = input.poloId;
  if (input.typologyGroupId != null) {
    body.typology_group_id = input.typologyGroupId;
  }
  if (input.uf != null) body.uf = input.uf;
  if (input.regiao != null) body.regiao = input.regiao;
  if (input.periodNumber != null) body.period_number = input.periodNumber;

  return request<{ id: number }>('/network/goals', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};
